import PropTypes from "prop-types";
import { Fragment, useCallback, useState } from "react";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import LazyLoad from "react-lazyload";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { addOptionsToCloundinaryURL } from "app/utils/image/cloudinaryUtils";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import "./ProgressiveImage.scss";

const MAX_WIDTHS = {
	small: 768,
	medium: 978,
	large: 1440,
	xlarge: 1440,
};

const ProgressiveImg = ({
	sizes = {},
	alt,
	src,
	thumbnail,
	offset = 800,
	backgroundPosition,
	overflow,
	onLoad,
	className,
	quality,
	cloudinaryOptions = [],
	productUri,
	cover,
	disableLazyLoad,
	fetchPriority = "auto",
}) => {
	const resolution = useSelector(state => state.resolution);
	const { track } = useAnalytics();
	const [loaded, setLoaded] = useState(false);

	const cloudinarify = useCallback(() => {
		let cloudinaryConfig = [];

		const size = sizes && sizes[resolution];

		if (size && size.width) {
			cloudinaryConfig.push(`w_${size.width}`);
		} else {
			cloudinaryConfig.push(`w_${MAX_WIDTHS[resolution]}`);
		}

		if (size && size.height) {
			cloudinaryConfig.push(`h_${size.height},c_fill,g_center`);
		}
		if (src) {
			return addOptionsToCloundinaryURL(src, cloudinaryConfig.concat(cloudinaryOptions), {
				quality,
			});
		}

		return "";
	}, [resolution, src, quality, sizes, cloudinaryOptions]);

	const onImageLoad = useCallback(() => {
		setLoaded(true);
		if (typeof onLoad === "function") {
			onLoad();
		}
	}, []);

	const imgClassname = classNames({
		"progressive-image__image": true,
		"progressive-image__image--cover": cover,
	});

	const thumbnailClassname = classNames("progressive-image__thumbnail", {
		"progressive-image__thumbnail--revealed": loaded,
	});

	const source = cloudinarify();

	const componentClassnames = classNames("progressive-image", className);

	const WrapperComponent = disableLazyLoad ? Fragment : LazyLoad;

	const style = {
		objectPosition: backgroundPosition || "center",
	};

	return (
		<div className={componentClassnames}>
			{resolution !== RESOLUTION.UNKNOWN && sizes[resolution] !== undefined ? (
				<WrapperComponent once offset={offset} overflow={overflow}>
					<img
						onError={() =>
							track("image_not_loaded", {
								url: source,
								uri: productUri,
							})
						}
						alt={alt}
						style={style}
						fetchPriority={fetchPriority}
						className={imgClassname}
						src={source}
						onLoad={onImageLoad}
						data-testid="progressive-image"
					/>
				</WrapperComponent>
			) : (
				false
			)}
			{thumbnail && (
				<img
					alt={alt}
					className={thumbnailClassname}
					src={`data:image/jpeg;base64,${thumbnail}`}
				/>
			)}
		</div>
	);
};

ProgressiveImg.propTypes = {
	sizes: PropTypes.shape({
		small: PropTypes.shape({
			height: PropTypes.number,
			width: PropTypes.number,
		}),
		medium: PropTypes.shape({
			height: PropTypes.number,
			width: PropTypes.number,
		}),
		large: PropTypes.shape({
			height: PropTypes.number,
			width: PropTypes.number,
		}),
		xlarge: PropTypes.shape({
			height: PropTypes.number,
			width: PropTypes.number,
		}),
	}),
	alt: PropTypes.string,
	src: PropTypes.string,
	backgroundPosition: PropTypes.string,
	thumbnail: PropTypes.string,
	offset: PropTypes.number,
	onLoad: PropTypes.func,
	className: PropTypes.string,
	disableLazyLoad: PropTypes.bool,
	quality: PropTypes.string,
	overflow: PropTypes.bool,
	cover: PropTypes.bool,
	cloudinaryOptions: PropTypes.array,
	productUri: PropTypes.string,
	fetchPriority: PropTypes.oneOf(["auto", "high", "low"]),
};

export default ProgressiveImg;
